import { useState, FC, useEffect, useCallback } from 'react'
import ReactDataSheet from 'react-datasheet'
import { produce } from 'immer'

import hearingMessage from '../../../data/hearing-message.json'
import { useAppSelector, useAppDispatch, CommonActions, ConfirmReplyActions } from '../../../stores'
import { MessageProvider } from '../../../services'
import {
  ConfirmDialog,
  SupplierHearingHeader,
  ErrorMessages,
  ErrorMessage
} from '../../../components/parts'
import { useAccessibleAuthority, useLeaveGuard, usePageTitle } from '../../../components/hooks'
import { GetHearingCapacityGateway } from '../../../gateway/supplier/GetHearingCapacityGateway'
import { PutHearingCapacityGateway } from '../../../gateway/supplier/PutHearingCapacityGateway'
import {
  PutSupplierHearingCapacityListReq,
  SupplierHearingCapacityListRes,
  SupplierMaxCapacity,
  TotalWorkQuantityByBranch
} from '@gateway.Supplier'

import { SufficiencyOfQuantityFooter } from './SufficiencyOfQuantityFooter'
import { SufficiencyOfQuantityGrid } from './SufficiencyOfQuantityGrid'
import { SupplierCapacityGrid } from './SupplierCapacityGrid'
import { DispatchQuantityGrid } from './DispatchQuantityGrid'
import { SufficiencyOfQuantityRateGrid } from './SufficiencyOfQuantityRateGrid'

import './SufficiencyOfQuantity.css'
import { Authority } from '../../../gateway/system/Authority'

export type CellValue = number | null
export interface GridElement
  extends ReactDataSheet.Cell<GridElement, CellValue> {
  value: CellValue
}
// 業者最大能力
export interface M1GridDataState {
  grid: GridElement[][]
  supplierMaxCapacityList: SupplierMaxCapacity[]
  totalWorkQuantityByBranchList: TotalWorkQuantityByBranch[]
  supplierMaxCapacityListLastMonth: SupplierMaxCapacity[]
  newAddFlgs: boolean[]
}
// 入場可能数
export interface M2GridDataState {
  grid: GridElement[][]
  ourCompanyCapacityList: SupplierMaxCapacity[]
  ourCompanyCapacityListLastMonth: SupplierMaxCapacity[]
  newAddFlgs: boolean[]
}
// 労務充足状況
export interface M3GridDataState {
  grid: GridElement[][]
  sufficiencyOfQuantityList: SupplierMaxCapacity[]
}
// 労務充足率
export interface M4GridDataState {
  grid: GridElement[][]
  sufficiencyOfQuantityRateList: SupplierMaxCapacity[]
}

interface GridDataState {
  M1GridDataState: M1GridDataState
  M2GridDataState: M2GridDataState
  M3GridDataState: M3GridDataState
  M4GridDataState: M4GridDataState
}

const GRID_DATA_STATE: GridDataState = {
  M1GridDataState: {
    grid: [],
    supplierMaxCapacityList: [],
    totalWorkQuantityByBranchList: [],
    supplierMaxCapacityListLastMonth: [],
    newAddFlgs: []
  },
  M2GridDataState: {
    grid: [],
    ourCompanyCapacityList: [],
    ourCompanyCapacityListLastMonth: [],
    newAddFlgs: []
  },
  M3GridDataState: {
    grid: [],
    sufficiencyOfQuantityList: []
  },
  M4GridDataState: {
    grid: [],
    sufficiencyOfQuantityRateList: []
  }
}

const MONTHS = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3]
const YEARS = 6

const SUPPLIER_HEARING_CAPACITY_LIST_RES: SupplierHearingCapacityListRes = {
  simulationYm: '',
  supplierCd: '',
  supplierBranchCd: '',
  factoryCd: '',
  expenseCd: '',
  addStsCd: '',
  addSts: '',
  addStsCdBeforeChange: null,
  addStsCdAfterChange: null,
  totalWorkQuantityByBranchList: [],
  supplierMaxCapacityList: [],
  ourCompanyCapacityList: []
}

export const SufficiencyOfQuantity: FC = () => {
  useAccessibleAuthority(Authority.Builder, Authority.Material, Authority.Branch, Authority.Supplier)
  usePageTitle('入場可能数')
  const dispatch = useAppDispatch()

  const regStatus = useAppSelector((state) => state.confirmreply.regStatus)

  const {
    replyYm,
    simulationYm,
    supplierCd,
    factoryCd,
    expenseCd,
    expense,
    supplierNm,
    branchCd,
    branchNm,
    factoryNm,
    supplierBranchCd,
    supplierBranchNm
  } = useAppSelector((state) => state.confirmreply.confirmReplyInfo)

  const replyStsCd = useAppSelector((state) => state.confirmreply.replyStsCd)
  const userProp = useAppSelector((state) => state.common.userProp)
  const displayStartYear = useAppSelector((state) => state.common.displayStartYear)

  const initialData = useAppSelector((x) => x.common.initialData)
  const codeNames = initialData.codeNames('SI01')

  const [confirmDialogSignInShow, setConfirmDialogSignInShow] = useState(false)
  const [confirmDialogTempSaveShow, setConfirmDialogTempSaveShow] =
    useState(false)
  const [notificationMessages, setNotificationMessages] = useState<ErrorMessage[]>([])
  const [errorMessages, setErrorMessages] = useState<ErrorMessage[]>([])

  const [gridDataState, setGridDataState] = useState(GRID_DATA_STATE)
  const [supplierHearingCapacityListRes, setSupplierHearingCapacityListRes] =
    useState(SUPPLIER_HEARING_CAPACITY_LIST_RES)
  const [
    supplierHearingCapacityListResLastMonth,
    setSupplierHearingCapacityListResLastMonth
  ] = useState(SUPPLIER_HEARING_CAPACITY_LIST_RES)

  // 変更検知
  const [isChanged, setIsChanged] = useState(false)

  // 過去月の判定フラグ
  const date = new Date()
  const currentYear = date.getFullYear()
  const currentMonth = date.getMonth() + 1
  const simulatedYear = Number(simulationYm.substring(0, 4))
  const simulatedMonth = Number(simulationYm.substring(4, simulationYm.length))
  const lastMonthFlg = simulatedYear < currentYear || (simulatedYear === currentYear && simulatedMonth < currentMonth)

  // 過去月または(業者ユーザかつ回答済)または確認済または(支店ユーザかつ自支店以外)の場合、入力項目を非活性にする
  const nonCtrlFlg =
    lastMonthFlg ||
    (userProp?.authority === Authority.Supplier && replyStsCd === '5') ||
    replyStsCd === '7' ||
    (userProp?.authority === Authority.Branch && userProp?.branchCode !== branchCd)

  const [, resetLeaveGuard] = useLeaveGuard([isChanged], '内容が編集中です。このページを離れますか？')

  // 行追加ボタン押下
  const handleOnAddRowBtnClick = () => {
    setIsChanged(true)
    setGridDataState(
      produce((draft) => {
        const girdCell: GridElement[] = []
        const gridM3Cell: GridElement[] = []
        const monthValArr: number[] = []

        for (let i = 0; i < draft.M1GridDataState.grid[0].length; i++) {
          girdCell.push({ value: 0, readOnly: onDataModifiableFn(i, simulationYm, displayStartYear, userProp?.authority) })
          gridM3Cell.push({ value: null, readOnly: true })
          monthValArr.push(0)
        }

        // 業者最大能力
        const supplierMaxCapacityObj: SupplierMaxCapacity = {
          branchCd: '',
          hearingkbn: '5',
          remark: '',
          hiddenFlg: 0,
          delFlg: 0,
          notDelFlg: 0,
          workQuantityByMonth: monthValArr,
          updDtm: null
        }

        // 入場可能数
        const ourCompanyObj: SupplierMaxCapacity = {
          branchCd: '',
          hearingkbn: '6',
          remark: '',
          hiddenFlg: 0,
          delFlg: 0,
          notDelFlg: 0,
          workQuantityByMonth: monthValArr,
          updDtm: null
        }

        // M1 Grid Add
        draft.M1GridDataState.grid.splice(
          draft.M1GridDataState.grid.length - 1,
          0,
          girdCell
        )

        draft.M1GridDataState.supplierMaxCapacityList.push(
          supplierMaxCapacityObj
        )
        draft.M1GridDataState.newAddFlgs.push(true)

        // M2 Grid Add
        draft.M2GridDataState.grid.splice(
          draft.M2GridDataState.grid.length - 1,
          0,
          girdCell
        )
        draft.M2GridDataState.ourCompanyCapacityList.push(ourCompanyObj)
        draft.M2GridDataState.newAddFlgs.push(true)

        // M3 Grid Add
        draft.M3GridDataState.grid.splice(
          draft.M3GridDataState.grid.length - 1,
          0,
          gridM3Cell
        )
        draft.M3GridDataState.sufficiencyOfQuantityList.push(
          supplierMaxCapacityObj
        )

        // M4 Grid Add
        draft.M4GridDataState.grid.splice(
          draft.M4GridDataState.grid.length - 1,
          0,
          gridM3Cell
        )
        draft.M4GridDataState.sufficiencyOfQuantityRateList.push(
          supplierMaxCapacityObj
        )
      })
    )
  }

  // 削除チェックボックス変更
  const handleOnDeleteM1CheckboxChange = (
    selectedRowIndex: number,
    selected: boolean
  ) => {
    setIsChanged(true)
    setGridDataState(
      produce((draft) => {
        draft.M1GridDataState.supplierMaxCapacityList[selectedRowIndex].delFlg =
          Number(selected)
        draft.M2GridDataState.ourCompanyCapacityList[selectedRowIndex].delFlg =
          Number(selected)
      })
    )
  }

  // 業者最大能力備考変更
  const handleOnM1RemarkInputChange = (rowNo: number, val: string) => {
    setIsChanged(true)
    setGridDataState(
      produce((draft) => {
        draft.M1GridDataState.supplierMaxCapacityList[rowNo].remark = val
      })
    )
  }

  // 支店DRL変更
  const handleOnM1BranchDropDownChange = (rowNo: number, val: string) => {
    setIsChanged(true)
    setGridDataState(
      produce((draft) => {
        draft.M1GridDataState.supplierMaxCapacityList[rowNo].branchCd = val
        draft.M2GridDataState.ourCompanyCapacityList[rowNo].branchCd = val
        draft.M3GridDataState.sufficiencyOfQuantityList[rowNo].branchCd = val
        draft.M4GridDataState.sufficiencyOfQuantityRateList[rowNo].branchCd = val

        // 労務充足状況、充足率の再計算
        const monthValArr: number[] = []
        for (let m = 0; m < YEARS * MONTHS.length; m++) {
          monthValArr.push(0)
        }
        // 対象支店の支店別労務数合計の合計値
        const sumTotalWQByBranch: number[] = [...monthValArr]
        // 入場可能数の合計値
        const sumOurCompanyCapacity: number[] = [...monthValArr]

        const _totalWorkQuantityByBranchList = draft.M1GridDataState.totalWorkQuantityByBranchList ?? []
        draft.M2GridDataState.ourCompanyCapacityList.forEach((ourCompanyCapacity, rowIndex) => {
          // 対象支店の支店別労務数合計を取得
          const _totalWorkQuantityByBranch = _totalWorkQuantityByBranchList.filter((obj) => obj.branchCd === ourCompanyCapacity.branchCd)

          // 支店別労務数合計．月別労務数
          const totalWQByBranchArr =
        _totalWorkQuantityByBranchList.length !== 0 && _totalWorkQuantityByBranch.length !== 0
          ? _totalWorkQuantityByBranch[0].workQuantityByMonth
          : monthValArr

          // 入場可能数．月別労務数
          ourCompanyCapacity.workQuantityByMonth.forEach(
            (valByMonth, columnIndex) => {
              // 計算に使用する値が0またはNULLの場合、計算しない
              const isNullVal = valByMonth === 0 ||
                valByMonth === null ||
                totalWQByBranchArr[columnIndex] === 0 ||
                totalWQByBranchArr[columnIndex] === null

              // 入場可能数一覧.入場可能数.月別労務数 - 支店別労務数合計一覧.支店別労務数合計.月別労務数
              draft.M3GridDataState.grid[rowIndex][columnIndex].value = isNullVal
                ? null
                : valByMonth - totalWQByBranchArr[columnIndex]

              // (支店別労務数合計一覧.支店別労務数合計.月別労務数 / 入場可能数一覧.入場可能数.月別労務数)*100
              draft.M4GridDataState.grid[rowIndex][columnIndex].value = isNullVal
                ? null
                : Math.ceil((totalWQByBranchArr[columnIndex] / valByMonth) * 100)

              sumOurCompanyCapacity[columnIndex] += valByMonth ?? 0
              sumTotalWQByBranch[columnIndex] += totalWQByBranchArr[columnIndex] ?? 0
            }
          )

          for (let i = 0; i < draft.M4GridDataState.grid[0].length; i++) {
            // 労務充足状況は合計値を設定する
            let m3Sum = 0
            for (let j = 0; j < draft.M3GridDataState.grid.length - 1; j++) {
              m3Sum += Number(draft.M3GridDataState.grid[j][i].value ?? 0)
            }
            draft.M3GridDataState.grid[draft.M3GridDataState.grid.length - 1][i] = {
              value: m3Sum,
              readOnly: true
            }

            // 労務充足率は入場可能数、支店別労務数合計の合算値から合計を算出する
            const m4Sum = sumTotalWQByBranch[i] === 0 || sumOurCompanyCapacity[i] === 0
              ? null
              : Math.ceil((sumTotalWQByBranch[i] / sumOurCompanyCapacity[i]) * 100)

            draft.M4GridDataState.grid[draft.M4GridDataState.grid.length - 1][i] = {
              value: m4Sum,
              readOnly: true
            }
          }
        })
      })
    )
  }

  // 業者最大能力セル変更
  const handleM1CellsChanged = (
    arrayOfChanges: ReactDataSheet.CellsChangedArgs<GridElement, CellValue>
  ) => {
    if (arrayOfChanges[0]?.value?.toString() !== '' && isNaN(Number(arrayOfChanges[0]?.value))) return

    setIsChanged(true)
    setGridDataState(
      produce((draft) => {
        arrayOfChanges.forEach(({ row, col, value }) => {
        // 業者最大能力の設定
          const preCellVal = Number(
            draft.M1GridDataState.grid[row][col].value ?? 0
          )
          const currentCellVal = arrayOfChanges[0]?.value?.toString() === '' ? null : Number(value ?? 0)
          draft.M1GridDataState.grid[row][col].value = value
          draft.M1GridDataState.supplierMaxCapacityList[row].workQuantityByMonth[col] =
           currentCellVal

          const gridLength = gridDataState.M1GridDataState.grid.length

          // M1 Grid Sum
          const preCellTotalVal = Number(
            draft.M1GridDataState.grid[gridLength - 1][col].value ?? 0
          )
          const currentCellTotalVal =
            preCellTotalVal - preCellVal + (currentCellVal ?? 0)
          draft.M1GridDataState.grid[gridLength - 1][col].value =
            currentCellTotalVal
        })
      })
    )
  }

  // 入場可能数備考変更
  const handleOnM2RemarkInputChange = (rowNo: number, val: string) => {
    setIsChanged(true)
    setGridDataState(
      produce((draft) => {
        draft.M2GridDataState.ourCompanyCapacityList[rowNo].remark = val
      })
    )
  }

  // 入場可能セル変更
  const handleM2CellsChanged = (
    arrayOfChanges: ReactDataSheet.CellsChangedArgs<GridElement, CellValue>
  ) => {
    if (arrayOfChanges[0]?.value?.toString() !== '' && isNaN(Number(arrayOfChanges[0]?.value))) return

    setIsChanged(true)
    setGridDataState(
      produce((draft) => {
        arrayOfChanges.forEach(({ row, col, value }) => {
          // 入場可能数を変更
          const preCellVal = Number(
            draft.M2GridDataState.grid[row][col].value ?? 0
          )
          const currentCellVal = arrayOfChanges[0]?.value?.toString() === '' ? null : Number(value ?? 0)
          draft.M2GridDataState.grid[row][col].value = value
          draft.M2GridDataState.ourCompanyCapacityList[row].workQuantityByMonth[col] =
            currentCellVal

          // 対象支店の支店別労務数合計を取得
          const totalWQByBranch = draft.M1GridDataState.totalWorkQuantityByBranchList
            .filter((obj) => obj.branchCd === draft.M1GridDataState.supplierMaxCapacityList[row].branchCd)

          const totalWQByBranchVal =
          totalWQByBranch.length === 0
            ? 0
            : totalWQByBranch[0].workQuantityByMonth[col]

          // 労務充足状況の設定
          const preM3CellVal = Number(
            draft.M3GridDataState.grid[row][col].value ?? 0
          )
          const currentM3CellVal =
            currentCellVal === null || currentCellVal === 0 || totalWQByBranchVal === 0
              ? null
              : currentCellVal - totalWQByBranchVal
          draft.M3GridDataState.grid[row][col].value = currentM3CellVal

          // 労務充足率の設定
          const currentM4CellVal =
            currentCellVal === null || currentCellVal === 0 || totalWQByBranchVal === 0
              ? null
              : Math.ceil((totalWQByBranchVal / currentCellVal) * 100)
          draft.M4GridDataState.grid[row][col].value = currentM4CellVal

          // 合計行を変更
          const gridLength = gridDataState.M2GridDataState.grid.length
          const preCellTotalVal = Number(
            draft.M2GridDataState.grid[gridLength - 1][col].value ?? 0
          )
          const currentCellTotalVal = preCellTotalVal - preCellVal + (currentCellVal ?? 0)
          draft.M2GridDataState.grid[gridLength - 1][col].value = currentCellTotalVal

          // M3 Grid Sum
          const preM3CellTotalVal = Number(
            draft.M3GridDataState.grid[gridLength - 1][col].value ?? 0
          )
          const currentM3CellTotalVal =
            preM3CellTotalVal - preM3CellVal + (currentM3CellVal ?? 0)
          draft.M3GridDataState.grid[gridLength - 1][col].value =
            currentM3CellTotalVal

          // M4 Grid Sum
          // 労務充足率の合計値は全体の合計を使用し再計算
          let sumTotalWQByBranch = 0
          draft.M2GridDataState.ourCompanyCapacityList.forEach((ourCompanyCapacity) => {
            // 対象支店の支店別労務数合計を取得
            const totalWQByBranch = draft.M1GridDataState.totalWorkQuantityByBranchList
              .filter((obj) => obj.branchCd === ourCompanyCapacity.branchCd)

            sumTotalWQByBranch += totalWQByBranch.length === 0 ? 0 : totalWQByBranch[0].workQuantityByMonth[col]
          })

          draft.M4GridDataState.grid[gridLength - 1][col].value =
            sumTotalWQByBranch === 0 || currentCellTotalVal === 0
              ? 0
              : Math.ceil((sumTotalWQByBranch / currentCellTotalVal) * 100)
        })
      })
    )
  }

  // 登録ボタン押下
  const handleOnSaveBtnClick = () => {
    // 1.カレンダ部相関チェック
    if (!checkM1AndM2GridCellVal()) {
      const args = ['入場可能数', '業者最大能力']
      setErrorMessages([
        {
          id: 'E006',
          content: MessageProvider.provide('E006', ...args)
        }
      ])
      return
    }

    // 2.支店重複チェック
    if (!checkBranches()) {
      const args = ['明細内', '支店']
      setErrorMessages([
        {
          id: 'E005',
          content: MessageProvider.provide('E005', ...args)
        }
      ])
      return
    }

    setConfirmDialogSignInShow(true)
  }

  // 一時保存ボタン押下
  const handleOnTempSaveBtnClick = () => {
    // 1.カレンダ部相関チェック
    if (!checkM1AndM2GridCellVal()) {
      const args = ['入場可能数', '業者最大能力']
      setErrorMessages([
        {
          id: 'E006',
          content: MessageProvider.provide('E006', ...args)
        }
      ])
      return
    }

    // 2.支店重複チェック
    if (!checkBranches()) {
      const args = ['明細内', '支店']
      setErrorMessages([
        {
          id: 'E005',
          content: MessageProvider.provide('E005', ...args)
        }
      ])
      return
    }

    setConfirmDialogTempSaveShow(true)
  }

  // 登録確認ダイアログOKボタン押下
  const dialogConfirmSignInBtn = () => {
    setConfirmDialogSignInShow(false)

    const workCapacityList: SupplierMaxCapacity[] = []

    // 業者決定物件
    gridDataState.M1GridDataState.supplierMaxCapacityList.forEach((obj) => {
      // State.更新日時＝NULLかつState.削除フラグ=1(削除)の行は対象外とする
      if (!(obj.updDtm === null && obj.delFlg === 1)) {
        const workQuantity = Object.assign({}, obj)
        workCapacityList.push(workQuantity)
      }
    })

    // 業者追加物件
    gridDataState.M2GridDataState.ourCompanyCapacityList.forEach((obj) => {
      // State.更新日時＝NULLかつState.削除フラグ=1(削除)の行は対象外とする
      if (!(obj.updDtm === null && obj.delFlg === 1)) {
        const workQuantity = Object.assign({}, obj)
        workCapacityList.push(workQuantity)
      }
    })

    const putSupplierHearingCapacityListReq: PutSupplierHearingCapacityListReq = {
      simulationYm: supplierHearingCapacityListRes.simulationYm,
      supplierCd: supplierHearingCapacityListRes.supplierCd,
      supplierBranchCd: supplierHearingCapacityListRes.supplierBranchCd,
      factoryCd: supplierHearingCapacityListRes.factoryCd,
      expenseCd: supplierHearingCapacityListRes.expenseCd,
      addStsCdBeforeChange: supplierHearingCapacityListRes.addStsCd,
      // 回答ステータスコードが"8"(事前調整)の場合は登録ステータスに "4"(事前調整) それ以外は "3"(登録済) を設定する
      addStsCdAfterChange: replyStsCd === '8' ? '4' : '3',
      workCapacityList,
      displayStartYear
    }

    dispatch(CommonActions.setIsLoading(true))
    PutHearingCapacityGateway.putSupplierHearingCapacityList(
      { data: putSupplierHearingCapacityListReq }
    )
      .then((res) => {
        if (!res.error) {
          fetchHearingCapacityList()
          if (isChanged) {
            resetLeaveGuard()
          }
          setNotificationMessages([
            {
              id: 'I004',
              content: MessageProvider.provide('I004', '登録')
            }
          ])
        } else {
          if (Array.isArray(res.messages) && res.messages.length > 0) {
            const errorMessages = res.messages.map<ErrorMessage>((message) => {
              return {
                id: message.id,
                content: message.message
              }
            })
            setErrorMessages(errorMessages)
          }
        }
      })
      .finally(() => {
        dispatch(CommonActions.setIsLoading(false))
      })
  }

  // 一時保存確認ダイアログOKボタン押下
  const dialogConfirmTempSaveInBtn = () => {
    setConfirmDialogTempSaveShow(false)

    const workCapacityList: SupplierMaxCapacity[] = []

    // 業者決定物件
    gridDataState.M1GridDataState.supplierMaxCapacityList.forEach((obj) => {
      // State.更新日時＝NULLかつState.削除フラグ=1(削除)の行は対象外とする
      if (!(obj.updDtm === null && obj.delFlg === 1)) {
        const workQuantity = Object.assign({}, obj)
        workCapacityList.push(workQuantity)
      }
    })

    // 業者追加物件
    gridDataState.M2GridDataState.ourCompanyCapacityList.forEach((obj) => {
      // State.更新日時＝NULLかつState.削除フラグ=1(削除)の行は対象外とする
      if (!(obj.updDtm === null && obj.delFlg === 1)) {
        const workQuantity = Object.assign({}, obj)
        workCapacityList.push(workQuantity)
      }
    })

    const putSupplierHearingCapacityListReq: PutSupplierHearingCapacityListReq = {
      simulationYm: supplierHearingCapacityListRes.simulationYm,
      supplierCd: supplierHearingCapacityListRes.supplierCd,
      supplierBranchCd: supplierHearingCapacityListRes.supplierBranchCd,
      factoryCd: supplierHearingCapacityListRes.factoryCd,
      expenseCd: supplierHearingCapacityListRes.expenseCd,
      addStsCdBeforeChange: supplierHearingCapacityListRes.addStsCd,
      // 回答ステータスコードが"8"(事前調整)の場合は登録ステータスに "4"(事前調整) それ以外は "2"(一時保存) を設定する
      addStsCdAfterChange: replyStsCd === '8' ? '4' : '2',
      workCapacityList,
      displayStartYear
    }

    dispatch(CommonActions.setIsLoading(true))
    PutHearingCapacityGateway.putSupplierHearingCapacityList(
      { data: putSupplierHearingCapacityListReq }
    )
      .then((res) => {
        if (!res.error) {
          fetchHearingCapacityList()
          if (isChanged) {
            resetLeaveGuard()
          }
          setNotificationMessages([
            {
              id: 'I004',
              content: MessageProvider.provide('I004', '一時保存')
            }
          ])
        } else {
          if (Array.isArray(res.messages) && res.messages.length > 0) {
            const errorMessages = res.messages.map<ErrorMessage>((message) => {
              return {
                id: message.id,
                content: message.message
              }
            })
            setErrorMessages(errorMessages)
          }
        }
      })
      .finally(() => {
        dispatch(CommonActions.setIsLoading(false))
      })
  }

  // カレンダ部相関チェック
  const checkM1AndM2GridCellVal = () => {
    // (1)チェック条件
    // ・同一支店コードで、各IndexごとにState．業者最大能力．月別労務数＜State．入場可能数．月別労務数エラーとする
    const gridM1RowLength =
      gridDataState.M1GridDataState.supplierMaxCapacityList.length
    for (let row = 0; row < gridM1RowLength; row++) {
      if (gridDataState.M1GridDataState.supplierMaxCapacityList[row].delFlg === 1) {
        continue
      }

      const gridM1ColLength = gridDataState.M1GridDataState.grid[row].length
      for (let col = 0; col < gridM1ColLength; col++) {
        const girdM1CellVal = Number(
          gridDataState.M1GridDataState.grid[row][col].value ?? 0
        )
        const gridM2CellVal = Number(
          gridDataState.M2GridDataState.grid[row][col].value ?? 0
        )
        if (girdM1CellVal < gridM2CellVal) {
          return false
        }
      }
    }
    return true
  }

  // 支店重複チェック
  const checkBranches = () => {
    let hasError = true
    gridDataState.M1GridDataState.supplierMaxCapacityList.forEach((supplierMaxCapacity) => {
      const length = gridDataState.M1GridDataState.supplierMaxCapacityList
        .filter((obj) => obj.branchCd === supplierMaxCapacity.branchCd && obj.delFlg === 0)

      if (length.length > 1 && supplierMaxCapacity.delFlg === 0) {
        hasError = false
      }
    })
    return hasError
  }

  // データ取得
  const fetchHearingCapacityList = useCallback(() => {
    // シミュレーション年月度の当月度データの取得
    dispatch(CommonActions.setIsLoading(true))
    GetHearingCapacityGateway.getSupplierHearingCapacityList(
      simulationYm,
      supplierCd,
      factoryCd,
      expenseCd,
      supplierBranchCd,
      displayStartYear
    )
      .then((res) => {
        if (!res.error) {
          setSupplierHearingCapacityListRes(
            res.data ?? SUPPLIER_HEARING_CAPACITY_LIST_RES
          )
        } else {
          const messages = res.messages.map((item) => ({
            id: item.id,
            content: item.message
          }))
          setErrorMessages(messages)
        }
      })
      .finally(() => {
        dispatch(CommonActions.setIsLoading(false))
      })

    // シミュレーション年月度の前月度データの取得
    const simulatedYear = Number(simulationYm.substring(0, 4))
    const simulatedMonth = Number(
      simulationYm.substring(4, simulationYm.length)
    )
    // 先月の取得
    const simulationYmLastMonth = getPreMonth(simulatedYear, simulatedMonth)
    dispatch(CommonActions.setIsLoading(true))
    GetHearingCapacityGateway.getSupplierHearingCapacityList(
      simulationYmLastMonth,
      supplierCd,
      factoryCd,
      expenseCd,
      supplierBranchCd,
      displayStartYear
    )
      .then((res) => {
        if (!res.error) {
          setSupplierHearingCapacityListResLastMonth(
            res.data ?? SUPPLIER_HEARING_CAPACITY_LIST_RES
          )
        } else {
          const messages = res.messages.map((item) => ({
            id: item.id,
            content: item.message
          }))
          setErrorMessages(messages)
        }
      })
      .finally(() => {
        dispatch(CommonActions.setIsLoading(false))
      })
  }, [expenseCd, factoryCd, simulationYm, supplierCd])

  // #region useEffect functions start
  useEffect(() => {
    // データストアチェック．業者ヒアリング遷移パラメータ．パラメータが設定されていない場合、エラーページへ遷移する
    const isPageError =
      regStatus === undefined ||
      regStatus === null ||
      simulationYm === '' ||
      supplierCd === '' ||
      factoryCd === '' ||
      expenseCd === '' ||
      replyYm === '' ||
      expense === '' ||
      supplierNm === '' ||
      branchCd === '' ||
      branchNm === '' ||
      supplierBranchCd === ''
    if (isPageError) {
      const pageParam = {
        regStatus,
        simulationYm,
        supplierCd,
        factoryCd,
        expenseCd,
        replyYm,
        expense,
        supplierNm,
        branchCd,
        branchNm,
        supplierBranchCd
      }
      throw new Error(`PageError Occurred. ${JSON.stringify(pageParam)}`)
    }
  }, [
    regStatus,
    simulationYm,
    supplierCd,
    factoryCd,
    expenseCd,
    replyYm,
    expense,
    supplierNm,
    branchCd,
    branchNm,
    supplierBranchCd
  ])

  useEffect(() => {
    fetchHearingCapacityList()
    return () => {
      dispatch(ConfirmReplyActions.setSupplierMaxCapacityLocation(0))
    }
  }, [])

  useEffect(() => {
    setGridDataState(
      produce((draft) => {
        const supplierMaxCapacityList =
          supplierHearingCapacityListRes.supplierMaxCapacityList
        const totalWorkQuantityByBranchList =
          supplierHearingCapacityListRes.totalWorkQuantityByBranchList
        const ourCompanyCapacityList =
          supplierHearingCapacityListRes.ourCompanyCapacityList
        const supplierMaxCapacityListLastMonth =
          supplierHearingCapacityListResLastMonth.supplierMaxCapacityList
        const ourCompanyCapacityListLastMonth =
          supplierHearingCapacityListResLastMonth.ourCompanyCapacityList

        // M1GridDataSate init
        draft.M1GridDataState.grid = createData(
          supplierMaxCapacityList,
          simulationYm,
          'M1',
          nonCtrlFlg,
          displayStartYear,
          userProp?.authority
        )
        draft.M1GridDataState.supplierMaxCapacityList = supplierMaxCapacityList.map((item) => ({
          ...item,
          delFlg: 0
        }))
        draft.M1GridDataState.totalWorkQuantityByBranchList = totalWorkQuantityByBranchList
        draft.M1GridDataState.supplierMaxCapacityListLastMonth = supplierMaxCapacityListLastMonth
        const newAddFlgs = supplierMaxCapacityList.map(() => {
          return false
        })
        draft.M1GridDataState.newAddFlgs = newAddFlgs

        // M2GridDataSate init
        draft.M2GridDataState.grid = createData(
          ourCompanyCapacityList,
          simulationYm,
          'M2',
          nonCtrlFlg,
          displayStartYear,
          userProp?.authority
        )
        draft.M2GridDataState.ourCompanyCapacityList = ourCompanyCapacityList.map((item) => ({
          ...item,
          delFlg: 0
        }))
        draft.M2GridDataState.ourCompanyCapacityListLastMonth = ourCompanyCapacityListLastMonth
        draft.M2GridDataState.newAddFlgs = newAddFlgs

        // M3GridDataSate init
        draft.M3GridDataState.grid = createData(
          ourCompanyCapacityList,
          simulationYm,
          'M3',
          nonCtrlFlg,
          displayStartYear,
          userProp?.authority,
          totalWorkQuantityByBranchList
        )
        draft.M3GridDataState.sufficiencyOfQuantityList = ourCompanyCapacityList

        // M4GridDataSate init
        draft.M4GridDataState.grid = createData(
          ourCompanyCapacityList,
          simulationYm,
          'M4',
          nonCtrlFlg,
          displayStartYear,
          userProp?.authority,
          totalWorkQuantityByBranchList
        )
        draft.M4GridDataState.sufficiencyOfQuantityRateList = ourCompanyCapacityList
      })
    )
  }, [
    simulationYm,
    supplierHearingCapacityListRes.ourCompanyCapacityList,
    supplierHearingCapacityListRes.supplierMaxCapacityList,
    supplierHearingCapacityListRes.totalWorkQuantityByBranchList,
    supplierHearingCapacityListResLastMonth.ourCompanyCapacityList,
    supplierHearingCapacityListResLastMonth.supplierMaxCapacityList
  ])
  // #endregion useEffect functions end

  return (
    <>
      <SupplierHearingHeader
        regStatus={supplierHearingCapacityListRes.addStsCd}
        promptInfo={hearingMessage.sufficiencyOfQuantity.headerPromptInfo}
        enableFixContentRegistration
        supplierHeaderInfo={{
          replyYm,
          supplierNm,
          expense,
          branchNm,
          factoryNm,
          supplierBranchNm
        }}
      />
      <SupplierCapacityGrid
        years={YEARS}
        months={MONTHS}
        simulationYm={simulationYm}
        gridDataState={gridDataState.M1GridDataState}
        nonCtrlFlg={nonCtrlFlg}
        branches={codeNames}
        onAddRowBtnClick={handleOnAddRowBtnClick}
        onDeleteCheckboxChange={handleOnDeleteM1CheckboxChange}
        onCellsChanged={handleM1CellsChanged}
        onRemarkInputChange={handleOnM1RemarkInputChange}
        onBranchDropDownChange={handleOnM1BranchDropDownChange}
        expenseCd={expenseCd}
        displayStartYear={displayStartYear}
      />
      <DispatchQuantityGrid
        years={YEARS}
        months={MONTHS}
        simulationYm={simulationYm}
        gridDataState={gridDataState.M2GridDataState}
        nonCtrlFlg={nonCtrlFlg}
        branches={codeNames}
        onCellsChanged={handleM2CellsChanged}
        onRemarkInputChange={handleOnM2RemarkInputChange}
        expenseCd={expenseCd}
        displayStartYear={displayStartYear}
      />
      <SufficiencyOfQuantityGrid
        years={YEARS}
        months={MONTHS}
        simulationYm={simulationYm}
        gridDataState={gridDataState.M3GridDataState}
        branches={codeNames}
        expenseCd={expenseCd}
        displayStartYear={displayStartYear}
      />
      <SufficiencyOfQuantityRateGrid
        years={YEARS}
        months={MONTHS}
        simulationYm={simulationYm}
        gridDataState={gridDataState.M4GridDataState}
        branches={codeNames}
        displayStartYear={displayStartYear}
      />
      <SufficiencyOfQuantityFooter
        nonCtrlFlg={nonCtrlFlg}
        onSaveBtnClick={handleOnSaveBtnClick}
        onTempSaveBtnClick={handleOnTempSaveBtnClick}
      />

      {confirmDialogSignInShow && (
        <ConfirmDialog
          title="登録"
          open={confirmDialogSignInShow}
          renderCloseButton
          id={'I007'}
          args={['登録']}
          onClickOk={dialogConfirmSignInBtn}
          onClickCancel={() => setConfirmDialogSignInShow(false)}
        />
      )}
      {confirmDialogTempSaveShow && (
        <ConfirmDialog
          title="一時保存"
          open={confirmDialogTempSaveShow}
          renderCloseButton
          id={'I007'}
          args={['一時保存']}
          onClickOk={dialogConfirmTempSaveInBtn}
          onClickCancel={() => setConfirmDialogTempSaveShow(false)}
        />
      )}
      <ErrorMessages type="notification" messages={notificationMessages} />
      <ErrorMessages type="dialog" messages={errorMessages} />
    </>
  )
}

/**
 * Create GridData
 * @param supplierMaxCapacityList
 * @param simulationYm
 * @param gridType
 * @param nonCtrlFlg
 * @param totalWorkQuantityByBranchList
 * @returns gridData
 */
const createData = (
  supplierMaxCapacityList: SupplierMaxCapacity[],
  simulationYm: string,
  gridType: string,
  nonCtrlFlg: boolean,
  displayStartYear: string,
  authority?: Authority,
  totalWorkQuantityByBranchList?: TotalWorkQuantityByBranch[]
) => {
  const gridData: GridElement[][] = []

  // 労務数一覧が空の場合、合計行のみを作成する
  if (supplierMaxCapacityList.length === 0) {
    gridData.push([])
    for (let i = 0; i < YEARS * MONTHS.length; i++) {
      gridData[0][i] = {
        value: 0,
        readOnly: true
      }
    }
    return gridData
  }

  // セルの活性状態を初期化
  for (let r = 0; r < supplierMaxCapacityList.length; r++) {
    gridData[r] = []
    for (let fy = 0; fy < YEARS; fy++) {
      for (let m = 0; m < MONTHS.length; m++) {
        if (gridType === 'M1' || gridType === 'M2') {
          gridData[r][fy * 12 + m] = {
            value: null,
            readOnly: onDataModifiableFn((fy * 12 + m), simulationYm, displayStartYear, authority) || nonCtrlFlg
          }
        }
        if (gridType === 'M3' || gridType === 'M4') {
          gridData[r][fy * 12 + m] = {
            value: null,
            readOnly: true
          }
        }
      }
    }
  }

  if (gridType === 'M1' || gridType === 'M2') {
    supplierMaxCapacityList.forEach((supplierMaxCapacityObj, rowIndex) => {
      const workQuantityByMonthArr = supplierMaxCapacityObj.workQuantityByMonth
      workQuantityByMonthArr.forEach((valByMonth, columnIndex) => {
        gridData[rowIndex][columnIndex].value = valByMonth
      })
    })
  }

  const monthValArr: number[] = []
  for (let m = 0; m < YEARS * MONTHS.length; m++) {
    monthValArr.push(0)
  }

  // 業者最大能力の合計値
  const sumTotalWQByBranch: number[] = [...monthValArr]
  // 対象支店の支店別労務数合計の合計値
  const sumMaxCapacity: number[] = [...monthValArr]

  if (gridType === 'M3' || gridType === 'M4') {
    const _totalWorkQuantityByBranchList = totalWorkQuantityByBranchList ?? []

    supplierMaxCapacityList.forEach((supplierMaxCapacityObj, rowIndex) => {
      // 対象支店の支店別労務数合計を取得
      const _totalWorkQuantityByBranch = _totalWorkQuantityByBranchList.filter((obj) => obj.branchCd === supplierMaxCapacityObj.branchCd)

      // 支店別労務数合計．月別労務数
      const totalWQByBranchArr =
        _totalWorkQuantityByBranchList.length !== 0 && _totalWorkQuantityByBranch.length !== 0
          ? _totalWorkQuantityByBranch[0].workQuantityByMonth
          : monthValArr

      // 業者最大能力．月別労務数
      supplierMaxCapacityObj.workQuantityByMonth.forEach(
        (valByMonth, columnIndex) => {
          // 計算に使用する値が0またはNULLの場合、計算しない
          const isNullVal = valByMonth === 0 ||
            valByMonth === null ||
            totalWQByBranchArr[columnIndex] === 0 ||
            totalWQByBranchArr[columnIndex] === null

          gridType === 'M3'
            // 業者最大能力一覧.業者最大能力.月別労務数 - 支店別労務数合計一覧.支店別労務数合計.月別労務数
            ? (gridData[rowIndex][columnIndex].value =
              isNullVal ? null : valByMonth - totalWQByBranchArr[columnIndex])
            // (支店別労務数合計一覧.支店別労務数合計.月別労務数 / 業者最大能力一覧.業者最大能力.月別労務数)*100
            : (gridData[rowIndex][columnIndex].value =
              isNullVal ? null : Math.ceil((totalWQByBranchArr[columnIndex] / valByMonth) * 100))

          sumMaxCapacity[columnIndex] += valByMonth ?? 0
          sumTotalWQByBranch[columnIndex] += totalWQByBranchArr[columnIndex] ?? 0
        }
      )
    })
  }

  // 合計のRowを追加
  gridData.push([])

  // 合計の計算
  if (gridType === 'M4') {
    // 労務充足率は業者最大能力、支店別労務数合計の合算値から合計を算出する
    for (let i = 0; i < gridData[0].length; i++) {
      const sum = sumTotalWQByBranch[i] === 0 || sumMaxCapacity[i] === 0
        ? 0
        : Math.ceil((sumTotalWQByBranch[i] / sumMaxCapacity[i]) * 100)

      gridData[gridData.length - 1][i] = {
        value: sum,
        readOnly: true
      }
    }
  } else {
    for (let i = 0; i < gridData[0].length; i++) {
      let sum = 0
      for (let j = 0; j < gridData.length - 1; j++) {
        sum += Number(gridData[j][i].value ?? 0)
      }
      gridData[gridData.length - 1][i] = {
        value: sum,
        readOnly: true
      }
    }
  }

  return gridData
}

/**
 * 前月度の取得
 * @param year
 * @param month
 * @returns 前月度
 */
const getPreMonth = (year: number, month: number) => {
  let year2 = year
  let month2 = month - 1
  if (month2 === 0) {
    year2 = year2 - 1
    month2 = 12
  }

  let month2Str = `${month2}`
  if (month2 < 10) {
    month2Str = `0${month2}`
  }
  return `${year2}${month2Str}`
}

// カレンダの活性状態を設定
const onDataModifiableFn = (i: number, simulationYm: string, displayStartYear: string, authority?: Authority) => {
  const simulatedYear = Number(simulationYm.substring(0, 4))
  const simulatedMonth = Number(simulationYm.substring(4, simulationYm.length))
  const year = new Date().getFullYear()
  const month = new Date().getMonth() + 1

  // シミュレーション年月度が過去の場合すべて非活性
  if (simulatedYear < year ||
      (simulatedYear === year && simulatedMonth < month)) {
    return true
  }

  // 過去月を非活性
  // 社内ユーザは過去月も入力可能とする
  if (authority === Authority.Builder || authority === Authority.Material || authority === Authority.Branch) {
    return false
  } else {
    // 表示開始年度から当年までの差分年数を算出する（年度は4月開始）
    let diffYear = year - Number(displayStartYear)
    if (month < 4) {
      // 1～3月は年度の10～12か月目（+9）に変換し、0始まりの配列位置にするため-1する
      diffYear = diffYear - 1
      return i < month + 9 - 1 + diffYear * 12
    } else {
      // 4月以降は4月を年度の1か月目（-3）として、0始まりの配列位置にするため-1する
      return i < month - 3 - 1 + diffYear * 12
    }
  }
}
